import {
  IEvent,
  Plugin as MggpPlugin,
  Player,
  PlayerConfig,
} from 'mggp-player';
import {
  apiChannelToChannel,
  apiProgramToProgram,
  getProgramState,
} from './transform';
import {
  ChannelType,
  EPG2_EVENT,
  EPG2_PROPERTY,
  IChannel,
  IEpgApi,
  IEpgTimeData,
  InteractiveChannelType,
  IOnAirProgramData,
  IProgram,
  ISetPlayingData,
  PLAYER_EVENT,
  PLAYER_PROPERTY,
  ProgramState,
} from './types';

const TIME_UPDATE_INTERVAL = 1000;

export default class MggpPluginEpg2 extends MggpPlugin {
  private timeOffset: number = 0;
  private currentChannel: IChannel | null = null;
  private playingProgram: IProgram | null = null;
  private airProgram: IProgram | null = null;
  private nexProgram: IProgram | null = null;
  private timeUpdateId: number | undefined;
  private epgApi: IEpgApi | null = null;

  constructor(player: Player, config: PlayerConfig) {
    super(player, config);

    this.player.registerProperty(PLAYER_PROPERTY.currentTimestamp, 0, true);
    this.player.registerProperty(PLAYER_PROPERTY.currentChannel, null, true);
    this.player.registerProperty(PLAYER_PROPERTY.currentProgram, null, true);
    this.player.registerProperty(EPG2_PROPERTY.playingProgram, null, true);

    const timeData = this.player.get<IEpgTimeData | null>(
      EPG2_PROPERTY.epgTime
    );
    if (timeData) {
      this.onEPGTime({ data: timeData });
    } else {
      this.player.on(EPG2_EVENT.epgTime, this.onEPGTime);
    }

    this.epgApi = this.player.get(EPG2_PROPERTY.api);
    if (!this.epgApi) {
      this.player.on(EPG2_EVENT.api, this.onEpgApi);
    }

    const setPlayingData = this.player.get<ISetPlayingData | null>(
      EPG2_PROPERTY.setPlaying
    );
    if (setPlayingData) {
      this.onEPGSetPlaying({ data: setPlayingData });
    }

    const onAirProgramData = this.player.get<IOnAirProgramData | null>(
      EPG2_PROPERTY.onAirProgram
    );
    if (onAirProgramData) {
      this.onEPGAirProgram({ data: onAirProgramData });
    }

    this.player.on(EPG2_EVENT.setPlaying, this.onEPGSetPlaying);
    this.player.on(EPG2_EVENT.onAirProgram, this.onEPGAirProgram);
    this.player.on(PLAYER_EVENT.playingProgram, this.onPlayerPlayingProgram);
    this.player.on(
      PLAYER_EVENT.interactiveChannelStartTime,
      this.onInteractiveChannelStartTime
    );
    this.timeUpdateId = window.setInterval(
      this.onTimeUpdate,
      TIME_UPDATE_INTERVAL
    );

    // console.log(this.player);
    // this.player.on('epg2:*', (event) => {
    //   console.log('!!!!!!!!event', event);
    // });

    // this.player.on('property:epg2:*', (event) => {
    //   console.log('!!!!!!!!prop', event.name, event.data);
    // });
  }

  dispose = () => {
    this.player.off(EPG2_EVENT.epgTime, this.onEPGTime);
    this.player.off(EPG2_EVENT.setPlaying, this.onEPGSetPlaying);
    this.player.off(EPG2_EVENT.onAirProgram, this.onEPGAirProgram);
    this.player.off(PLAYER_EVENT.playingProgram, this.onPlayerPlayingProgram);
    this.player.off(EPG2_EVENT.api, this.onEpgApi);
    this.player.off(
      PLAYER_EVENT.interactiveChannelStartTime,
      this.onInteractiveChannelStartTime
    );
    window.clearInterval(this.timeUpdateId);
  };

  private onEPGTime = (event: IEvent<IEpgTimeData>) => {
    this.timeOffset = event.data.timestamp - Math.floor(Date.now() / 1000);
    this.player.set(PLAYER_PROPERTY.currentTimestamp, event.data.timestamp);
  };

  private onEpgApi = (event: IEvent<IEpgApi>) => {
    this.epgApi = event.data;
  };

  private onEPGSetPlaying = (event: IEvent<ISetPlayingData>) => {
    const channel = apiChannelToChannel(
      event.data.channel,
      () => this.nexProgram,
      event.data.channelGenreId
    );
    const isInit = !this.currentChannel;
    const isChannelChange = this.currentChannel?.id !== channel.id;
    let programToPlay: IProgram | null = null;

    if (event.data.program) {
      const apiProgram = event.data.program;

      programToPlay = apiProgramToProgram({
        apiProgram,
        parentChannel: channel,
        getProgramState: () =>
          getProgramState({
            start: apiProgram.start_timestamp,
            end: apiProgram.end_timestamp,
            current: this.getCurrentTimestamp(),
          }),
      });

      if (isChannelChange && event.data.onAir) {
        channel.currentProgram = programToPlay;
        channel._addProgram(programToPlay);
        this.player.set(PLAYER_PROPERTY.currentProgram, channel.currentProgram);
      }
    }

    if (isChannelChange) {
      this.currentChannel = channel;
      this.player.set(PLAYER_PROPERTY.currentChannel, this.currentChannel);
    } else if (programToPlay) {
      let channelType: InteractiveChannelType;

      if (channel.type === ChannelType.REGULAR_CHANNEL) {
        channelType = ChannelType.VOD_CHANNEL;
      } else {
        channelType = channel.type;
      }

      if (
        programToPlay.startTimestamp === this.playingProgram?.startTimestamp
      ) {
        // if user has clicked on the same program as is playing now
        if (
          this.player.get(PLAYER_PROPERTY.channelState) === 'timeshift' &&
          programToPlay.state === ProgramState.CURRENT
        ) {
          // if this is an air program and player in timeshift mode
          this.player.emit(PLAYER_EVENT.backOnAir(channelType));
        }
        // otherwise do nothing
      } else {
        // if clicked program is some other program, play it, or got back on air if this is current program
        if (programToPlay.state === ProgramState.CURRENT) {
          this.player.emit(PLAYER_EVENT.backOnAir(channelType));
        } else {
          this.player.emit(
            PLAYER_EVENT.playProgram(channelType),
            programToPlay
          );
        }
      }
    }

    if (event.data.onAir && !programToPlay) {
      this.player.set(PLAYER_PROPERTY.currentProgram, null);
    }

    if (!isInit && isChannelChange) {
      this.player.emit(PLAYER_EVENT.channelSwitch, this.currentChannel);
    }
  };

  private onEPGAirProgram = (event: IEvent<IOnAirProgramData>) => {
    if (this.currentChannel && event.data) {
      const { start_timestamp } = event.data;

      if (
        this.currentChannel.currentProgram &&
        start_timestamp !== this.currentChannel.currentProgram.startTimestamp
      ) {
        this.currentChannel.currentProgram.state = ProgramState.PAST;
      }

      if (this.airProgram?.startTimestamp !== start_timestamp) {
        this.airProgram = apiProgramToProgram({
          apiProgram: event.data,
          parentChannel: this.currentChannel,
          getProgramState: () => ProgramState.CURRENT,
        });

        this.currentChannel.currentProgram = this.airProgram;
        this.currentChannel._addProgram(this.airProgram);
        this.player.set(PLAYER_PROPERTY.currentProgram, this.airProgram);
        this.player.emit(PLAYER_EVENT.currentProgramChanged, this.airProgram);

        const current = this.getCurrentTimestamp();
        this.player.set(
          PLAYER_PROPERTY.currentProgramTime,
          current - this.airProgram.startTimestamp
        );
      }
    }
  };

  private onPlayerPlayingProgram = (event: IEvent<IProgram>) => {
    if (
      this.currentChannel &&
      event.data &&
      this.playingProgram?.startTimestamp !== event.data.startTimestamp
    ) {
      this.player.set(EPG2_PROPERTY.playingProgram, {
        channelId: this.currentChannel.id,
        programStartTimestamp: event.data.startTimestamp,
      });
      this.playingProgram = event.data;

      this.requestNextProgram();
    }
  };

  private onInteractiveChannelStartTime = (event: IEvent<number | null>) => {
    if (event.data) {
      this.requestStartProgramForInteractiveChannel(event.data);
      this.player.off(
        PLAYER_EVENT.interactiveChannelStartTime,
        this.onInteractiveChannelStartTime
      );
    }
  };

  private onTimeUpdate = () => {
    const current = this.getCurrentTimestamp();

    this.player.set(PLAYER_PROPERTY.currentTimestamp, current);

    if (this.airProgram) {
      let currentTime = current - this.airProgram.startTimestamp;

      if (currentTime > this.airProgram.duration) {
        currentTime = this.airProgram.duration;
      }

      this.player.set(PLAYER_PROPERTY.currentProgramTime, currentTime);
      this.airProgram.state = getProgramState({
        start: this.airProgram.startTimestamp,
        end: this.airProgram.endTimestamp,
        current,
      });
    }

    if (this.playingProgram) {
      this.playingProgram.state = getProgramState({
        start: this.playingProgram.startTimestamp,
        end: this.playingProgram.endTimestamp,
        current,
      });
    }

    if (this.nexProgram) {
      this.nexProgram.state = getProgramState({
        start: this.nexProgram.startTimestamp,
        end: this.nexProgram.endTimestamp,
        current,
      });
    }
  };

  private getCurrentTimestamp = (): number => {
    return Math.floor(Date.now() / 1000) + this.timeOffset;
  };

  private requestNextProgram = (): void => {
    const playingProgram = this.playingProgram;

    if (playingProgram && this.epgApi) {
      this.epgApi
        .fetchProgram(
          playingProgram.channel.id,
          playingProgram.endTimestamp + 1
        )
        .then((apiProgram) => {
          if (
            // check if playingProgram was not changed during the request
            playingProgram.startTimestamp ===
            this.playingProgram?.startTimestamp
          ) {
            this.nexProgram = apiProgram
              ? apiProgramToProgram({
                  apiProgram,
                  parentChannel: this.playingProgram!.channel,
                  getProgramState: () =>
                    getProgramState({
                      start: apiProgram.start_timestamp,
                      end: apiProgram.end_timestamp,
                      current: this.getCurrentTimestamp(),
                    }),
                })
              : null;
          } else {
            this.requestNextProgram();
          }
        });
    }
  };

  private requestStartProgramForInteractiveChannel = (startTime: number) => {
    if (this.epgApi) {
      this.epgApi
        .fetchProgram(this.currentChannel!.id, startTime)
        .then((apiProgram) => {
          if (apiProgram && this.currentChannel) {
            const program = apiProgramToProgram({
              apiProgram,
              parentChannel: this.currentChannel,
              getProgramState: () =>
                getProgramState({
                  start: apiProgram.start_timestamp,
                  end: apiProgram.end_timestamp,
                  current: this.getCurrentTimestamp(),
                }),
            });

            this.currentChannel._addProgram(program);
            this.player.emit(PLAYER_EVENT.currentProgramListUpdated);
          }
        });
    }
  };
}
